import { Source, Layer } from 'react-map-gl/mapbox';
import type { LineLayerSpecification } from 'mapbox-gl';
import { useGcsStore } from '../../../store/useGcsStore';
import { getMapElementZIndex, MapElement } from '../../../types/ui/mapElement';
import type { MissionId } from '../../../types/gcs/mission';
import { getLockedSegmentAnchorLayerId } from './lockedSegmentAnchor';

interface SingleMissionRoutePolylineProps {
  gcsId: string;
  missionId: MissionId;
}

const createRouteLineLayer = (layerId: string, sourceId: string): LineLayerSpecification => ({
  id: layerId,
  type: 'line',
  source: sourceId,
  paint: {
    'line-color': '#ffffff',
    'line-width': 4,
    'line-opacity': 0.5
  }
});

const breakListByIndices = <T,>(nodes: readonly T[], indices: readonly number[]): T[][] => {
  const result: T[][] = [];
  let startIndex = 0;

  // Go through each index in the indices list
  for (const index of indices) {
    // Take a sublist from the current start index to the index (inclusive)
    result.push(nodes.slice(startIndex, index + 1));
    // Update startIndex to the next element after the current index
    startIndex = index + 1;
  }

  // Add the final sublist from the last index to the end of the nodes list
  result.push(nodes.slice(startIndex, nodes.length));

  return result;
};

export const SingleMissionRoutePolyline = ({ gcsId, missionId }: SingleMissionRoutePolylineProps) => {
  const routeData = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionRouteMap?.get(missionId);
  });

  const lockingInfo = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.missionLockingInfoMap?.get(missionId);
  });

  if (!routeData?.route || routeData.route.length === 0) {
    return null;
  }

  // Stack the route line just below the locked-segment lines, but only anchor to a locked-segment
  // layer that actually exists — a dangling beforeId makes mapbox-gl drop the layer entirely (ROC-90).
  const lockedSegmentAnchorLayerId = getLockedSegmentAnchorLayerId(
    gcsId,
    missionId,
    routeData.route.length,
    lockingInfo
  );

  // Break route into segments using routeBreakIndices
  const routeSegments =
    routeData.routeBreakIndices && routeData.routeBreakIndices.length > 0
      ? breakListByIndices(routeData.route, routeData.routeBreakIndices)
      : [routeData.route];

  return (
    <>
      {routeSegments.map((segment, segmentIndex) => {
        const coordinates = segment.map((waypoint) => [waypoint.location.lng, waypoint.location.lat]);
        const sourceId = `route-${gcsId}-${missionId}-segment-${segmentIndex}`;
        const layerId = `route-line-z${getMapElementZIndex(MapElement.MISSION_ROUTE)}-${gcsId}-${missionId}-${segmentIndex}`;

        const routeGeoJSON = {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates
          },
          properties: {
            missionId: routeData.missionId,
            segmentIndex
          }
        };

        return (
          <Source key={sourceId} id={sourceId} type='geojson' data={routeGeoJSON}>
            <Layer {...createRouteLineLayer(layerId, sourceId)} beforeId={lockedSegmentAnchorLayerId} />
          </Source>
        );
      })}
    </>
  );
};
